/**
 * AutomationsPage.tsx
 *
 * List-only page for SMS + Email automations at /marketing/automations.
 * Provides stats grid, channel filter (All/Email/SMS), status tabs,
 * search, create modal, and navigates to dedicated editor pages on row click.
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Play, Pause, Zap, Mail, MessageCircle, Search,
  FileText, Users, AlertTriangle,
} from 'lucide-react';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { useToast } from '@/hooks/useToast';
import { FilterTabs } from '@/components/ui/FilterTabs';
import type { BadgeVariant } from '@/types/common.types';
import { usePageContext } from '@/hooks/usePageContext';
import * as marketingSvc from '@/services/marketing.service';
import type { Automation, EmailAutomationStepHold } from '@/services/marketing.service';

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  stopped: 'error',
};

const STATUS_TABS = [
  { label: 'All', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Active', value: 'active' },
  { label: 'Paused', value: 'paused' },
  { label: 'Stopped', value: 'stopped' },
];

const CHANNEL_TABS = [
  { label: 'All', value: '' },
  { label: 'Email', value: 'email', icon: <Mail className="h-3.5 w-3.5" /> },
  { label: 'SMS', value: 'sms', icon: <MessageCircle className="h-3.5 w-3.5" /> },
];

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "Step 2", "Steps 2, 3" — or "Sending" when the API did not list the steps. */
function heldStepsLabel(steps: EmailAutomationStepHold[] | undefined): string {
  const orders = (steps ?? []).map((s) => s.stepOrder);
  if (orders.length === 0) return 'Sending';
  return `${orders.length === 1 ? 'Step' : 'Steps'} ${orders.join(', ')}`;
}

export default function AutomationsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const updateAIContext = usePageContext('Automations');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [page, setPage] = useState(1);
  const [channelPickerOpen, setChannelPickerOpen] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    updateAIContext({ dataSummary: 'Viewing automations list' });
  }, [updateAIContext]);

  const { data: statsData } = useQuery({
    queryKey: ['marketing', 'automations', 'stats'],
    queryFn: () => marketingSvc.getAutomationListStats(),
  });

  const { data } = useQuery({
    queryKey: ['marketing', 'automations', { search, status: statusFilter, channel: channelFilter, page }],
    queryFn: () =>
      marketingSvc.listAutomations({
        search: search || undefined,
        status: statusFilter || undefined,
        channel: channelFilter || undefined,
        page,
        perPage: 25,
      }),
  });

  const createMut = useMutation({
    mutationFn: (d: { name: string; channel: 'email' | 'sms' }) =>
      marketingSvc.createAutomation(d),
    onSuccess: (automation) => {
      qc.invalidateQueries({ queryKey: ['marketing', 'automations'] });
      setChannelPickerOpen(false);
      setNewName('');
      toast.success('Automation created');
      navigateToAutomation(automation);
    },
    onError: () => {
      toast.error('Failed to create automation');
    },
  });


  function navigateToAutomation(automation: Automation | { automationId: number; channel: 'email' | 'sms'; status?: string }) {
    const status = 'status' in automation ? automation.status : undefined;
    const base = `/marketing/automations/${automation.channel === 'sms' ? 'sms' : 'email'}/${automation.automationId}`;
    // Anything that has run has stats worth landing on — paused included; its
    // stats page carries Resume and Edit Automation. Only a draft has nothing
    // to show yet, so it opens straight in the editor.
    navigate(status && status !== 'draft' ? `${base}/stats` : base);
  }

  const automations = data?.data ?? [];
  const meta = data?.meta;
  const stats = statsData ?? { total: 0, active: 0, draft: 0, paused: 0, stopped: 0, totalEnrollments: 0, activeEnrollments: 0 };

  const columns: DataTableColumn<Automation>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (r) => (
        <div className="text-left">
          <span className="font-medium text-slate-900">{r.name}</span>
          {r.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{r.description}</p>}
        </div>
      ),
    },
    {
      key: 'channel',
      header: 'Channel',
      render: (r) => (
        <Badge variant={r.channel === 'sms' ? 'success' : 'info'}>
          {r.channel === 'sms' ? (
            <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> SMS</span>
          ) : (
            <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email</span>
          )}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant={STATUS_BADGE[r.status] ?? 'neutral'} dot>
            {capitalize(r.status)}
          </Badge>
          {/* SendGrid refused a step for a reason that is not the recipient's
              (e.g. an unverified sender): that step tries one contact an hour.
              Clears after the next send on it that goes through; the Stats
              page has the detail. */}
          {r.channel === 'email' && r.sendHoldReason ? (
            <span title={`${heldStepsLabel(r.sendHoldSteps)} held — SendGrid: ${r.sendHoldReason}`}>
              <Badge variant="warning">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                Sending held
              </Badge>
            </span>
          ) : null}
        </div>
      ),
    },
    { key: 'stepCount', header: 'Steps', render: (r) => r.stepCount ?? 0 },
    { key: 'enrolledCount', header: 'Enrolled', render: (r) => r.enrolledCount ?? 0 },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0 space-y-6">
        <PageHeader
          variant="compact"
          title="Automations"
          actions={(
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setChannelPickerOpen(true)}>
              New Automation
            </Button>
          )}
        />

        {/* Stats Grid */}
        <MetricCardGrid columns={6}>
          <StatCard label="Total Automations" value={stats.total} icon={<Zap className="h-5 w-5" />} tone="violet" />
          <StatCard label="Active" value={stats.active} icon={<Play className="h-5 w-5" />} tone="emerald" />
          <StatCard label="Draft" value={stats.draft} icon={<FileText className="h-5 w-5" />} tone="slate" />
          <StatCard label="Paused" value={stats.paused} icon={<Pause className="h-5 w-5" />} tone="amber" />
          <StatCard label="Active Enrollments" value={stats.activeEnrollments} icon={<Users className="h-5 w-5" />} tone="blue" />
          <StatCard label="Total Enrollments" value={stats.totalEnrollments} icon={<Users className="h-5 w-5" />} tone="sky" />
        </MetricCardGrid>

        {/* Filters + Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <FilterTabs
              tabs={CHANNEL_TABS}
              value={channelFilter}
              onChange={(v) => { setChannelFilter(v); setPage(1); }}
            />
            <FilterTabs
              tabs={STATUS_TABS}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
            />
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search automations..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 w-64"
            />
          </div>
        </div>
      </div>

      {/* Automations Table */}
      <div className="flex-1 min-h-0 mt-6">
        <DataTable
          keyField="automationId"
          columns={columns}
          data={automations}
          page={meta?.page ?? 1}
          pageSize={meta?.perPage ?? 25}
          total={meta?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => navigateToAutomation(r)}
          emptyTitle="No automations yet"
          emptyDescription="Create a multi-step drip sequence to engage patients automatically."
        />
      </div>

      {/* Create Automation Modal */}
      <Modal
        isOpen={channelPickerOpen}
        onClose={() => { setChannelPickerOpen(false); setNewName(''); }}
        title="Create New Automation"
        size="sm"
      >
        <div className="space-y-5">
          <FormField label="Automation Name" required>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Welcome Series, Appointment Reminders"
              autoFocus
            />
          </FormField>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-3">Select Channel</p>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                disabled={!newName.trim() || createMut.isPending}
                onClick={() => createMut.mutate({ name: newName.trim(), channel: 'email' })}
                className="group flex flex-col items-center gap-3 rounded-xl border-2 border-slate-200 bg-white p-6 transition-all hover:border-blue-500 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-100">
                  <Mail className="h-6 w-6" />
                </div>
                <span className="text-sm font-semibold text-slate-700 group-hover:text-blue-700">Create Email Automation</span>
              </button>
              <button
                type="button"
                disabled={!newName.trim() || createMut.isPending}
                onClick={() => createMut.mutate({ name: newName.trim(), channel: 'sms' })}
                className="group flex flex-col items-center gap-3 rounded-xl border-2 border-slate-200 bg-white p-6 transition-all hover:border-emerald-500 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-100">
                  <MessageCircle className="h-6 w-6" />
                </div>
                <span className="text-sm font-semibold text-slate-700 group-hover:text-emerald-700">Create SMS Automation</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>

    </div>
  );
}
